const LASER_REMOVED_TOKENS = new Set([
  'laser',
  'lasers',
  'machine',
  'machines',
  'device',
  'devices',
  'system',
  'systems',
  'equipment',
  'option',
  'options',
]);

export function normalizeLaserComparableValue(value: unknown): string {
  const normalized = String(value ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  if (!normalized) {
    return '';
  }

  return normalized
    .split(' ')
    .filter((token) => token && !LASER_REMOVED_TOKENS.has(token))
    .join(' ');
}

export function areLaserComparableValuesEqual(left: unknown, right: unknown): boolean {
  const normalizedLeft = normalizeLaserComparableValue(left);
  const normalizedRight = normalizeLaserComparableValue(right);
  return laserComparableEquals(normalizedLeft, normalizedRight);
}

export function findBestLaserComparableOption<T>(
  options: readonly T[],
  rawValue: unknown,
  getLabel: (option: T) => unknown,
  getValue?: (option: T) => unknown,
): T | null {
  const normalized = normalizeLaserComparableValue(rawValue);
  if (!isComparableLaserValue(normalized)) {
    return null;
  }

  const exactMatch = findExactLaserOptionMatch(options, normalized, getLabel, getValue);
  if (exactMatch) {
    return exactMatch;
  }

  const scoredMatch = findScoredLaserOptionMatch(options, normalized, getLabel, getValue);
  if (!scoredMatch) {
    return null;
  }

  return isAcceptedLaserOptionScore(scoredMatch.bestScore, scoredMatch.secondBestScore)
    ? scoredMatch.option
    : null;
}

function isComparableLaserValue(value: string): boolean {
  return !!value && value !== '__null__' && value !== '__undefined__';
}

function findExactLaserOptionMatch<T>(
  options: readonly T[],
  normalized: string,
  getLabel: (option: T) => unknown,
  getValue?: (option: T) => unknown,
): T | null {
  return (
    options.find((option) => {
      const optionLabel = normalizeLaserComparableValue(getLabel(option));
      const optionValue = normalizeLaserComparableValue(getValue ? getValue(option) : null);
      return (
        laserComparableEquals(optionLabel, normalized) ||
        laserComparableEquals(optionValue, normalized)
      );
    }) ?? null
  );
}

function laserComparableEquals(left: string, right: string): boolean {
  return left === right || compactComparableValue(left) === compactComparableValue(right);
}

function findScoredLaserOptionMatch<T>(
  options: readonly T[],
  normalized: string,
  getLabel: (option: T) => unknown,
  getValue?: (option: T) => unknown,
): { option: T; bestScore: number; secondBestScore: number } | null {
  let bestOption: T | null = null;
  let bestScore = 0;
  let secondBestScore = 0;

  options.forEach((option) => {
    const optionLabel = normalizeLaserComparableValue(getLabel(option));
    const optionValue = normalizeLaserComparableValue(getValue ? getValue(option) : null);
    const score = Math.max(
      computeLaserComparableScore(normalized, optionLabel),
      computeLaserComparableScore(normalized, optionValue),
    );

    if (score <= secondBestScore && score <= bestScore) {
      return;
    }

    if (score > bestScore) {
      secondBestScore = bestScore;
      bestScore = score;
      bestOption = option;
      return;
    }

    secondBestScore = score;
  });

  if (!bestOption) {
    return null;
  }

  return {
    option: bestOption,
    bestScore,
    secondBestScore,
  };
}

function isAcceptedLaserOptionScore(bestScore: number, secondBestScore: number): boolean {
  return bestScore >= 0.75 && bestScore - secondBestScore >= 0.08;
}

function compactComparableValue(value: string): string {
  return value.replace(/\s+/g, '');
}

function computeLaserComparableScore(left: string, right: string): number {
  if (!left || !right) {
    return 0;
  }

  if (left === right) {
    return 1;
  }

  const compactLeft = compactComparableValue(left);
  const compactRight = compactComparableValue(right);
  if (compactLeft === compactRight) {
    return 0.98;
  }

  if (
    compactLeft.length >= 4 &&
    compactRight.length >= 4 &&
    (compactLeft.includes(compactRight) || compactRight.includes(compactLeft))
  ) {
    return 0.92;
  }

  const maxLength = Math.max(compactLeft.length, compactRight.length);
  if (!maxLength) {
    return 0;
  }

  const distance = levenshteinDistance(compactLeft, compactRight);
  return 1 - distance / maxLength;
}

function levenshteinDistance(left: string, right: string): number {
  if (left === right) {
    return 0;
  }

  if (!left.length) {
    return right.length;
  }

  if (!right.length) {
    return left.length;
  }

  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  const current = new Array<number>(right.length + 1);

  for (let i = 0; i < left.length; i += 1) {
    current[0] = i + 1;
    for (let j = 0; j < right.length; j += 1) {
      const cost = left[i] === right[j] ? 0 : 1;
      current[j + 1] = Math.min(current[j]! + 1, previous[j + 1]! + 1, previous[j]! + cost);
    }

    for (let j = 0; j <= right.length; j += 1) {
      previous[j] = current[j]!;
    }
  }

  return previous[right.length]!;
}
